import { REGION_LABELS } from './regions.js';

/**
 * Which holidays belong to which place, for the Holidays page filter.
 *
 * ⚠️ A PLACE IS A COUNTRY *AND* A REGION. Not one or the other.
 *
 * The filter used to key on region alone, which was fine while every customer was
 * British. Rob added an Algerian location on 02/08/2026 and it broke twice over:
 *
 *   1. Algeria's holidays carry no region, so they produced no entry in the filter —
 *      there was no way to look at them.
 *   2. They then LEAKED into the England and Wales view, because `!h.region` was being
 *      read as "applies everywhere". A null region means "everywhere IN THIS COUNTRY",
 *      which is how a country with no sub-divisions is stored. It has never meant
 *      "every country on Earth".
 *
 * This mirrors `backend/services/holidays_util.holiday_applies` deliberately: the page
 * shows what the server would give someone working in that place, so the two must
 * answer the same question the same way.
 */

/** Stable identity for a place, safe to use as a <select> value and React key. */
export function placeKey(countryCode, region) {
  return `${countryCode || ''}|${region || ''}`;
}

export function parsePlace(key) {
  const [countryCode, region] = String(key ?? '').split('|');
  return { countryCode: countryCode || null, region: region || null };
}

/**
 * Does this holiday show in the view for `place`?
 *
 * - Company days apply to the whole company, so they appear in every view. Hiding them
 *   made an admin think their newly-added day hadn't saved.
 * - Country is compared ONLY when both sides have one. Rows created before the country
 *   column existed have none, and a filter that silently hid every legacy holiday would
 *   be a far worse bug than one that shows a few too many.
 * - A null region matches any region *within a matching country*.
 */
export function holidayMatchesPlace(holiday, place) {
  if (!holiday) return false;

  // ⚠️ `if (holiday.is_custom) return true;` USED TO LIVE HERE, and it hid the fault Rob
  // reported on 13/08/2026 rather than causing it. The page pretended every company day
  // applied everywhere while the SERVER filtered them like any other row — so a day
  // stamped with the wrong country looked present in the filter and was absent from the
  // list. Two layers answering the same question differently is worse than either
  // answer. A company day now goes through the same comparison, and "everyone" is
  // expressed the way it always was: no country and no region.
  const { countryCode, region } = place || {};
  const countryOk =
    !holiday.country_code || !countryCode || holiday.country_code === countryCode;
  const regionOk = !holiday.region || holiday.region === region;
  return countryOk && regionOk;
}

/** Every distinct place present in a list of holidays, company days excluded. */
export function placesIn(holidays) {
  const seen = new Map();
  (holidays || []).forEach((h) => {
    if (h.is_custom) return;
    const key = placeKey(h.country_code, h.region);
    if (!seen.has(key)) {
      seen.set(key, { key, countryCode: h.country_code || null, region: h.region || null });
    }
  });
  return [...seen.values()];
}

/**
 * A place, in words. Pure.
 *
 * ⚠️ MOVED OUT OF `HolidaysPage` ON 13/08/2026 because signup now asks the same question
 * ("who gets this day off?") and two screens naming the same place differently is the
 * fault this week has been spent on. `countryNames` is the code -> name map both screens
 * already fetch from `/holidays/countries`.
 *
 * ⚠️ THE COUNTRY IS ONLY PREFIXED WHEN THE COMPANY HAS MORE THAN ONE. A UK-only customer
 * reading "United Kingdom — Scotland" is being told something they already know, twice.
 */
export function placeLabel(place, countryNames = {}, multiCountry = false) {
  const { countryCode, region } = place || {};
  const regionName = region ? (REGION_LABELS[region] || region) : null;
  const countryName = countryCode ? (countryNames[countryCode] || countryCode) : null;
  if (regionName && multiCountry && countryName) return `${countryName} — ${regionName}`;
  if (regionName) return regionName;
  return countryName || 'Everyone';
}

/** The place a holiday belongs to. */
export const placeOf = (holiday) => ({
  key: placeKey(holiday?.country_code, holiday?.region),
  countryCode: holiday?.country_code || null,
  region: holiday?.region || null,
});
